import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { t } from '@lingui/macro'
import { createContext, Fragment, useContext, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { useVisualViewportHeight } from '@/@bothrs-ui/web/hooks'

import type {
  ComponentPropsWithoutRef,
  CSSProperties,
  Dispatch,
  KeyboardEvent,
  ReactNode,
  RefObject,
  SetStateAction,
} from 'react'

// ================================ Constants ================================

const DEFAULT_MIN_CHARS = 3

// ================================ Utilities ================================

/** Splits `text` into ordered segments around every case-insensitive match of `query`. */
export function highlightMatch(text: string, query: string): { text: string; isMatch: boolean }[] {
  if (!query) return [{ text, isMatch: false }]
  const lowerQuery = query.toLowerCase()
  // Escape regex metacharacters so the query is matched literally.
  const escaped = query.replaceAll(/[$()*+.?[\\\]^{|}]/g, '\\$&')
  // The capturing group keeps the matched substrings in the split result,
  // yielding alternating non-match/match segments with original casing.
  const segments = text
    .split(new RegExp(`(${escaped})`, 'gi'))
    .filter(Boolean)
    .map((part) => ({ text: part, isMatch: part.toLowerCase() === lowerQuery }))
  // `text === ''` splits to a single empty string that `filter` drops; fall
  // back to one segment so callers always receive a non-empty array.
  return segments.length > 0 ? segments : [{ text, isMatch: false }]
}

// ================================ Context ================================

type SearchComboboxContextValue = {
  id: string
  selectedName: string | null
  searchQuery: string
  onSearchQueryChange: (query: string) => void
  results: unknown[]
  getResultName: (result: unknown) => string
  showDropdown: boolean
  activeIndex: number
  setActiveIndex: Dispatch<SetStateAction<number>>
  /** Picks the option at `index`: a result, or the free text option right after them. */
  selectOption: (index: number) => void
  inputRef: RefObject<HTMLInputElement>
}

const SearchComboboxContext = createContext<SearchComboboxContextValue | null>(null)

const useSearchComboboxContext = () => {
  const context = useContext(SearchComboboxContext)
  if (!context) {
    throw new Error('SearchCombobox parts must be rendered inside a SearchCombobox')
  }

  return context
}

/* ------------------------------------------------------------------------------------------------- *
 * SearchCombobox
 * ------------------------------------------------------------------------------------------------- */

type SearchComboboxRootProps<T> = Omit<ComponentPropsWithoutRef<'div'>, 'onSelect' | 'results'> & {
  id: string
  selectedName: string | null
  results: T[]
  getResultName: (result: T) => string
  onSelect: (result: T) => void
  onSelectFreeText: (name: string) => void
  searchQuery: string
  onSearchQueryChange: (query: string) => void
  minChars?: number
}

/**
 * Generic autocomplete combobox with keyboard navigation, free-text
 * fallback, and ARIA combobox pattern. Domain-agnostic — the root holds the
 * search state, the parts render it.
 */
const SearchComboboxRoot = <T,>({
  id,
  selectedName,
  results,
  getResultName,
  onSelect,
  onSelectFreeText,
  searchQuery,
  onSearchQueryChange,
  minChars = DEFAULT_MIN_CHARS,
  style,
  children,
  ...props
}: SearchComboboxRootProps<T>) => {
  const [activeIndex, setActiveIndex] = useState(-1)
  const inputRef = useRef<HTMLInputElement>(null)
  const previousSelectedRef = useRef<string | null>(null)

  const viewportHeight = useVisualViewportHeight()

  const showDropdown = !selectedName && searchQuery.trim().length >= minChars

  // Auto-reset and refocus when selection is cleared from outside
  useEffect(() => {
    if (previousSelectedRef.current !== null && selectedName === null) {
      onSearchQueryChange('')
      requestAnimationFrame(() => inputRef.current?.focus())
    }
    previousSelectedRef.current = selectedName
  }, [selectedName, onSearchQueryChange])

  useEffect(() => {
    setActiveIndex(-1)
  }, [searchQuery])

  // Keep the active item visible when navigating by keyboard inside the
  // scrollable results container.
  useEffect(() => {
    if (activeIndex < 0) return
    document.querySelector(`#${CSS.escape(`${id}-option-${activeIndex}`)}`)?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex, id])

  const selectOption = (index: number) => {
    if (index < results.length) {
      onSelect(results[index])
    } else {
      onSelectFreeText(searchQuery.trim())
    }
    onSearchQueryChange('')
    setActiveIndex(-1)
  }

  return (
    <SearchComboboxContext.Provider
      value={{
        id,
        selectedName,
        searchQuery,
        onSearchQueryChange,
        results,
        getResultName: getResultName as (result: unknown) => string,
        showDropdown,
        activeIndex,
        setActiveIndex,
        selectOption,
        inputRef,
      }}
    >
      {/* TODO: only the results list reads --visual-vh; set it there rather than on the root. */}
      <StRoot style={{ '--visual-vh': `${viewportHeight}px`, ...style } as CSSProperties} {...props}>
        {children}
      </StRoot>
    </SearchComboboxContext.Provider>
  )
}

/* ------------------------------------------------------------------------------------------------- *
 * SearchCombobox.Label
 * ------------------------------------------------------------------------------------------------- */

type SearchComboboxLabelProps = ComponentPropsWithoutRef<typeof Span>

const SearchComboboxLabel = ({ children, ...props }: SearchComboboxLabelProps) => {
  const { id } = useSearchComboboxContext()

  return (
    <StLabel id={`${id}-label`} type='BodyLabel' {...props}>
      {children}
    </StLabel>
  )
}

/* ------------------------------------------------------------------------------------------------- *
 * SearchCombobox.Field
 * ------------------------------------------------------------------------------------------------- */

type SearchComboboxFieldProps = ComponentPropsWithoutRef<'div'>

/** The bordered row the input sits in, with whatever the call site puts next to it. */
const SearchComboboxField = ({ children, ...props }: SearchComboboxFieldProps) => (
  <StField {...props}>{children}</StField>
)

/* ------------------------------------------------------------------------------------------------- *
 * SearchCombobox.Adornment
 * ------------------------------------------------------------------------------------------------- */

type SearchComboboxAdornmentProps = ComponentPropsWithoutRef<'span'>

/** Content at the start of the field, before the input, e.g. a thumbnail. */
const SearchComboboxAdornment = ({ children, ...props }: SearchComboboxAdornmentProps) => (
  <StAdornment {...props}>{children}</StAdornment>
)

/* ------------------------------------------------------------------------------------------------- *
 * SearchCombobox.Input
 * ------------------------------------------------------------------------------------------------- */

type SearchComboboxInputProps = Omit<ComponentPropsWithoutRef<'input'>, 'value' | 'onChange'>

const SearchComboboxInput = (props: SearchComboboxInputProps) => {
  const {
    id,
    selectedName,
    searchQuery,
    onSearchQueryChange,
    results,
    showDropdown,
    activeIndex,
    setActiveIndex,
    selectOption,
    inputRef,
  } = useSearchComboboxContext()

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown) return
    const totalOptions = results.length + 1
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActiveIndex((index) => Math.min(index + 1, totalOptions - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex((index) => Math.max(index - 1, 0))
    } else if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault()
      selectOption(activeIndex)
    } else if (event.key === 'Escape') {
      event.preventDefault()
      onSearchQueryChange('')
      setActiveIndex(-1)
    }
  }

  return (
    <StInput
      ref={inputRef}
      type='text'
      data-1p-ignore
      autoComplete='off'
      role='combobox'
      aria-expanded={showDropdown}
      aria-controls={`${id}-listbox`}
      aria-autocomplete='list'
      aria-labelledby={`${id}-label`}
      aria-activedescendant={showDropdown && activeIndex >= 0 ? `${id}-option-${activeIndex}` : undefined}
      readOnly={!!selectedName}
      value={selectedName ?? searchQuery}
      onChange={(event_) => {
        if (!selectedName) {
          onSearchQueryChange(event_.target.value)
        }
      }}
      onKeyDown={handleKeyDown}
      {...props}
    />
  )
}

/* ------------------------------------------------------------------------------------------------- *
 * SearchCombobox.Icon
 * ------------------------------------------------------------------------------------------------- */

type SearchComboboxIconProps = ComponentPropsWithoutRef<typeof FontAwesomeIcon>

/** A decorative icon at the end of the field, e.g. the magnifying glass. */
const SearchComboboxIcon = (props: SearchComboboxIconProps) => (
  <StIconSlot>
    <FontAwesomeIcon {...props} />
  </StIconSlot>
)

/* ------------------------------------------------------------------------------------------------- *
 * SearchCombobox.ClearButton
 * ------------------------------------------------------------------------------------------------- */

type SearchComboboxClearButtonProps = ComponentPropsWithoutRef<'button'>

/** Clears the selection; `children` is the icon it shows. */
const SearchComboboxClearButton = ({ children, ...props }: SearchComboboxClearButtonProps) => (
  <StClearButton
    type='button'
    aria-label={t({
      id: 'search.combobox.clearButton',
      message: 'Selectie wissen',
    })}
    {...props}
  >
    {children}
  </StClearButton>
)

/* ------------------------------------------------------------------------------------------------- *
 * SearchCombobox.Results
 * ------------------------------------------------------------------------------------------------- */

type SearchComboboxResultsProps = ComponentPropsWithoutRef<'div'> & {
  /** Label of the option that adds the query as typed. */
  freeTextLabel: (query: string) => ReactNode
}

/**
 * The results dropdown and its screen reader count; renders nothing until the query is long enough.
 *
 * TODO: split into its own compound (list, option, free text option) once a
 * call site needs to arrange the options itself.
 */
const SearchComboboxResults = ({ freeTextLabel, ...props }: SearchComboboxResultsProps) => {
  const { id, searchQuery, results, getResultName, showDropdown, activeIndex, selectOption } =
    useSearchComboboxContext()

  return (
    <>
      <StLiveRegion role='status' aria-live='polite' aria-atomic='true'>
        {showDropdown ? t`${results.length} resultaten gevonden` : ''}
      </StLiveRegion>
      {showDropdown && (
        <StDropdown id={`${id}-listbox`} role='listbox' {...props}>
          <StScrollableResults>
            {results.map((result, index) => {
              const name = getResultName(result)
              const segments = highlightMatch(name, searchQuery)
              return (
                <StResultItem
                  key={`${name}-${index}`}
                  id={`${id}-option-${index}`}
                  role='option'
                  aria-selected={index === activeIndex}
                  data-active={index === activeIndex}
                  onClick={() => selectOption(index)}
                >
                  <Span type='BodyMediumRegular'>
                    {segments.map((segment, segmentIndex) =>
                      segment.isMatch ? (
                        <Span key={segmentIndex} type='BodyMediumBold'>
                          {segment.text}
                        </Span>
                      ) : (
                        <Fragment key={segmentIndex}>{segment.text}</Fragment>
                      ),
                    )}
                  </Span>
                </StResultItem>
              )
            })}
          </StScrollableResults>
          <StDivider />
          <StResultItem
            id={`${id}-option-${results.length}`}
            role='option'
            aria-selected={results.length === activeIndex}
            data-active={results.length === activeIndex}
            onClick={() => selectOption(results.length)}
          >
            <Span type='BodyMediumRegular'>{freeTextLabel(searchQuery)}</Span>
            <StAddIcon>
              <FontAwesomeIcon icon={['fal', 'plus']} />
            </StAddIcon>
          </StResultItem>
        </StDropdown>
      )}
    </>
  )
}

/* ------------------------------------------------------------------------------------------------- *
 * Compound export
 * ------------------------------------------------------------------------------------------------- */

export const SearchCombobox = Object.assign(SearchComboboxRoot, {
  Label: SearchComboboxLabel,
  Field: SearchComboboxField,
  Adornment: SearchComboboxAdornment,
  Input: SearchComboboxInput,
  Icon: SearchComboboxIcon,
  ClearButton: SearchComboboxClearButton,
  Results: SearchComboboxResults,
})

// ================================ Styled Components ================================

const StRoot = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space2};
`

const StLabel = styled(Span)`
  color: ${({ theme }) => theme.Colors.Neutrals.Darkest};
  text-transform: uppercase;
`

const StField = styled.div`
  display: flex;
  align-items: center;
  background: ${({ theme }) => theme.Colors.Background.White};
  border: 1px solid ${({ theme }) => theme.Colors.Neutrals.Light};
  border-radius: ${({ theme }) => theme.UI.BorderRadius.Small}px;

  &:focus-within {
    border-color: ${({ theme }) => theme.Colors.Theme.Primary};
  }
`

const StAdornment = styled.span`
  display: flex;
  flex-shrink: 0;
  padding-left: ${({ theme }) => theme.UI.SpacingPx.Space2};
`

const StInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: ${({ theme }) => `${theme.UI.SpacingPx.Space3} ${theme.UI.SpacingPx.Space4}`};
  padding-right: 0;
  border: none;
  background: none;
  font-size: 16px;
  line-height: 22px;
  font-weight: 400;
  color: ${({ theme }) => theme.Colors.Neutrals.Darkest};
  outline: none;
  box-sizing: border-box;

  &::placeholder {
    color: ${({ theme }) => theme.Colors.Neutrals.Dark};
  }
`

const StIconSlot = styled.span`
  flex-shrink: 0;
  color: ${({ theme }) => theme.Colors.Neutrals.Dark};
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: ${({ theme }) => theme.UI.SpacingPx.Space11};
  min-height: ${({ theme }) => theme.UI.SpacingPx.Space11};
  font-size: 16px;
`

const StClearButton = styled.button`
  flex-shrink: 0;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${({ theme }) => theme.Colors.Neutrals.Dark};
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: ${({ theme }) => theme.UI.SpacingPx.Space11};
  min-height: ${({ theme }) => theme.UI.SpacingPx.Space11};
  font-size: 16px;
`

const StLiveRegion = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`

const StDropdown = styled.div`
  background: ${({ theme }) => theme.Colors.Background.White};
  border: 1px solid ${({ theme }) => theme.Colors.Neutrals.Light};
  border-radius: ${({ theme }) => theme.UI.BorderRadius.Small}px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin-top: ${({ theme }) => theme.UI.SpacingPx.Space1};
`

const StScrollableResults = styled.div`
  overflow-y: auto;
  /* --visual-vh is set from JS (visualViewport.height) because iOS Capacitor
     does not resize the WebView when the keyboard opens, so dvh/vh stay full-size.
     45% keeps the list usable above the keyboard; 15rem caps it when closed. */
  max-height: min(15rem, calc(var(--visual-vh, 100vh) * 0.45));
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.Colors.Neutrals.Light};
    border-radius: 2px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }
`

const StResultItem = styled.button`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => `${theme.UI.SpacingPx.Space3} ${theme.UI.SpacingPx.Space4}`};
  background: none;
  border: none;
  border-bottom: 1px solid ${({ theme }) => theme.Colors.Neutrals.Lighter};
  cursor: pointer;
  text-align: left;

  &:last-child {
    border-bottom: none;
  }

  &:hover,
  &[data-active='true'] {
    background-color: ${({ theme }) => theme.Colors.Neutrals.Lighter};
  }
`

const StAddIcon = styled.span`
  color: ${({ theme }) => theme.Colors.Theme.Primary};
  display: flex;
  align-items: center;
  flex-shrink: 0;
`

const StDivider = styled.hr`
  margin: 0;
  border: none;
  border-top: 1px solid ${({ theme }) => theme.Colors.Neutrals.Light};
`
